import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import {
  buildSentryTracePropagationTargets,
  hydratePortalSentryUserFromStorage,
  initPortalSentry,
  setPortalSentryTag,
} from '@admin-panel/observability';
import { BrowserRouter } from 'react-router';
import { installPackingNavigationGuard } from '@admin-panel/pages/orders/packages/usePackingNavigationGuard';

import App from './app/Application';
import { applyPreviewParams, captureInstallPrompt } from './app/hubAlertSetup/hubAlertSetupState';
import { answerWindowModeQueries } from './app/hubAlerts/hubWindowMode';
import { IS_HUB_DEV_SERVER } from './app/hubAlerts/alertCenter/devServer';
import { ensureDeviceFingerprint, getDeviceFingerprint } from '@admin-panel/utils';

import './styles/app.css';

const tracePropagationTargets = buildSentryTracePropagationTargets(
  import.meta.env.VITE_GATEWAY_API_URL,
);

initPortalSentry(tracePropagationTargets);
// Local/Stage alert-setup preview controls; then keep Chrome's install prompt (it can fire before
// React renders) for the alert setup page.
applyPreviewParams();
captureInstallPrompt();
// A tapped order alert opens the installed Hub app, not a stray browser tab.
answerWindowModeQueries();

const bootstrap = async () => {
  // Local review page for the alert center (dev server only, no sign-in).
  if (IS_HUB_DEV_SERVER && window.location.pathname === '/__alerts-playground') {
    const { AlertCenterPlayground } =
      await import('./app/hubAlerts/alertCenter/AlertCenterPlayground');
    ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
      <StrictMode>
        <AlertCenterPlayground />
      </StrictMode>,
    );
    return;
  }
  installPackingNavigationGuard();
  await ensureDeviceFingerprint();
  setPortalSentryTag('device_id', getDeviceFingerprint());
  hydratePortalSentryUserFromStorage();
  const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
  root.render(
    <StrictMode>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </StrictMode>,
  );
};

void bootstrap();

// The push worker must be registered before a device can subscribe to order alerts. Opened
// without a session (an expired one clears storage without logging out), retire any alert
// subscription the previous user left on this device.
window.setTimeout(() => {
  void import('./app/hubAlerts/hubPushRegistration').then(
    ({ registerHubServiceWorker, retireStaleHubAlertToken }) => {
      void registerHubServiceWorker();
      if (!hasStoredSession()) {
        void retireStaleHubAlertToken();
      }
    },
  );
}, 0);

function hasStoredSession() {
  try {
    return Boolean(localStorage.getItem('user') && localStorage.getItem('at'));
  } catch {
    return false;
  }
}
